import { type FC, type ReactNode, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type EntitySchemaApiId, type EntitySchemaRequiredUpsertV1 } from '@/model/EntitySchema.model';
import { type EntitySchemaV1 } from '@/model/EntitySchema.model';
import { EntitySchemaContext } from './EntitySchemaContext';
import { useSelector } from 'react-redux';
import { selectTeam } from '@/reducers/team';

export const EntitySchemaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [entityFields, setEntityFields] = useState<Array<EntitySchemaV1>>([]);
  const [updateError, setUpdateError] = useState<boolean>(false);

  const team = useSelector(selectTeam);
  const isLoggedIn = Boolean(window.localStorage.getItem('token'));
  const disabled = !isLoggedIn || team == null;

  const {
    response: fieldsResponse,
    loading: entityLoading,
    invoke,
  } = useApiHook({
    endpoint: 'GET /api/v1/entity-schemas',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: true, slug: 'entity-schema-provider-load' } },
  });

  const { invoke: patchFieldRequired, loading: entityUpdating } = useApiHook({
    endpoint: 'PATCH /api/v1/entity-schemas/{entitySchemaApiId}',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: true, slug: 'entity-schema-provider-update' } },
  });

  const invokeRef = useRef(invoke);
  useEffect(() => {
    invokeRef.current = invoke;
  }, [invoke]);

  useEffect(() => {
    if (!disabled) {
      invokeRef.current();
    } else {
      setEntityFields([]);
      setUpdateError(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!disabled && fieldsResponse?.type === 'success') {
      const apiFields = fieldsResponse.body as Array<EntitySchemaV1>;
      setEntityFields(apiFields);
    }
  }, [fieldsResponse, disabled]);

  const setFieldRequired = useCallback(
    async (apiId: EntitySchemaApiId, required: boolean) => {
      if (disabled) {
        return;
      }

      setUpdateError(false);

      const requestBody: EntitySchemaRequiredUpsertV1 = { required };
      const response = await patchFieldRequired({
        path: { entitySchemaApiId: apiId },
        body: requestBody,
      });

      if (response?.type === 'success' && response.body) {
        const updatedField = response.body as EntitySchemaV1;
        setEntityFields((prevFields) =>
          prevFields.map((field) => (field.api_id === updatedField.api_id ? updatedField : field))
        );
        setUpdateError(false);
      } else {
        setUpdateError(true);
      }
    },
    [patchFieldRequired, disabled]
  );

  const contextValue = useMemo(
    () => ({
      entityFields,
      entityLoading: !disabled ? entityLoading : false,
      entityUpdating: !disabled ? entityUpdating : false,
      setFieldRequired,
      hasEntityError: !disabled ? fieldsResponse?.type === 'error' || updateError : false,
    }),
    [entityFields, entityLoading, entityUpdating, setFieldRequired, fieldsResponse?.type, updateError, disabled]
  );

  return <EntitySchemaContext.Provider value={contextValue}>{children}</EntitySchemaContext.Provider>;
};
